import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import Draggable from "react-draggable";
import { Button, Modal } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import "react-pdf/dist/Page/AnnotationLayer.css";

import Footer from "@/Components/Footer/Footer";

import ActionBarComponent from "../../common/ActionBarComponent";
import NavBar from "../../Navbars/main";
import Sidebar from "../../Sidebars/main";
import { api_without_cancellation } from "../../../api/api";
import PDF_LOAD_OPTIONS from "../../../Utils/pdfLoadOptions";
import "../../../Utils/pdfWorker";
import UploadIcon from "../../../../public/bp_assets/img/cloud_icon.svg";
import "../Documents.css";
import PdfNavigation from "../PdfNavigation";
import { usePdfTextSearch } from "../usePdfTextSearch";

import {
  defaultExpirationDate,
  FIELD_GROUPS,
  LETTER_PAGE,
  normalizeRecipients,
  requestErrorMessage,
} from "./esignConfig";
import "./EsignPage.css";

const fieldDefinitions = FIELD_GROUPS.flatMap((group) => group.fields);
const SINGLE_SELECTION_ROLES = new Set(["defendant", "otherparty"]);
const MIN_FIELD_RENDERED_WIDTH = 45;
const MIN_FIELD_RENDERED_HEIGHT = 20;
const getPaletteFieldLabel = (label) =>
  label.replace(/^(Client|Defendant|Other Party|Party)\s+/i, "");
const getApiFieldType = (semanticKey) =>
  typeof semanticKey === "string" && semanticKey.endsWith(".signature")
    ? "signature"
    : semanticKey?.endsWith(".date")
      ? "date"
      : "text";
const buildTemplatePayload = ({
  documentId,
  documentName,
  fields,
  isPermanent,
  pageId,
  slotId,
}) => ({
  doc_id: Number(documentId),
  name: documentName.trim(),
  fields: fields.map(({ semantic_key, position_x, position_y, width, height, page_no }) => ({
    field_type: getApiFieldType(semantic_key),
    semantic_key,
    position_x: Number(Number(position_x || 0).toFixed(4)),
    position_y: Number(Number(position_y || 0).toFixed(4)),
    width: Number(Number(width || 0).toFixed(2)),
    height: Number(Number(height || 0).toFixed(2)),
    page_no: Number(page_no),
  })),
  is_permanent: isPermanent,
  ...(isPermanent && {
    page_id: Number(pageId),
    slot_id: Number(slotId),
  }),
});

const serializeTemplatePayload = (payload) => JSON.stringify(payload);

const ESignPage = () => {
  const { clientId, caseId, docId, templateId: routeTemplateId } = useParams();
  const [searchParams] = useSearchParams();
  const editingTemplateId = routeTemplateId || searchParams.get("template_id");
  const [pdfViewport, setPdfViewport] = useState(null);
  const pageRefs = useRef(new Map());
  const nextFieldId = useRef(1);
  const savedTemplatePayloadRef = useRef(null);
  const resizeCleanupRef = useRef(null);

  const [pdfData, setPdfData] = useState(null);
  const [templateDocumentId, setTemplateDocumentId] = useState(null);
  const [documentName, setDocumentName] = useState("");
  const [numPages, setNumPages] = useState(0);
  const [pageDimensions, setPageDimensions] = useState({});
  const [pageWidth, setPageWidth] = useState(760);
  const [activePage, setActivePage] = useState(1);
  const [pdfDocument, setPdfDocument] = useState(null);
  const [pdfSearchTerm, setPdfSearchTerm] = useState("");
  const [defaultZoom, setDefaultZoom] = useState("fit");
  const [fields, setFields] = useState([]);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(true);
  const [pdfError, setPdfError] = useState("");
  const [loadingTemplate, setLoadingTemplate] = useState(Boolean(editingTemplateId));
  const [editingTemplateIsPermanent, setEditingTemplateIsPermanent] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState(editingTemplateId || null);

  const [mode, setMode] = useState("one-off");
  const [recipientGroups, setRecipientGroups] = useState([]);
  const [signers, setSigners] = useState([]);
  const [activeSignerKey, setActiveSignerKey] = useState("");
  const [recipientsLoading, setRecipientsLoading] = useState(true);
  const [expirationDate, setExpirationDate] = useState(defaultExpirationDate);

  const [locations, setLocations] = useState([]);
  const [locationLoading, setLocationLoading] = useState(true);
  const [selectedPageId, setSelectedPageId] = useState("");
  const [selectedSlotId, setSelectedSlotId] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [preparedTemplateId, setPreparedTemplateId] = useState(null);
  const [preparedSendPayload, setPreparedSendPayload] = useState(null);
  const [result, setResult] = useState(null);
  const [sendConfirmationMessage, setSendConfirmationMessage] = useState("");
  const effectiveDocumentId = docId || templateDocumentId;
  const [debouncedPdfSearchTerm] = useDebounce(pdfSearchTerm, 500);
  const { matchingPages: pdfSearchPages, progress: pdfSearchProgress } = usePdfTextSearch(
    pdfDocument,
    debouncedPdfSearchTerm
  );

  useEffect(() => {
    setActiveTemplateId(editingTemplateId || null);
    savedTemplatePayloadRef.current = null;
  }, [editingTemplateId]);

  useEffect(
    () => () => {
      resizeCleanupRef.current?.();
    },
    []
  );

  useEffect(() => {
    const warnAboutUnsavedFields = (event) => {
      if (!fields.length || result?.tone === "success") return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnAboutUnsavedFields);
    return () => window.removeEventListener("beforeunload", warnAboutUnsavedFields);
  }, [fields.length, result?.tone]);

  useEffect(() => {
    if (!effectiveDocumentId) {
      setLoadingPdf(Boolean(editingTemplateId));
      return undefined;
    }

    let pdfUrl;
    const fetchPdf = async () => {
      setLoadingPdf(true);
      setPdfError("");
      setPdfDocument(null);
      setPdfSearchTerm("");
      setActivePage(1);
      try {
        const response = await api_without_cancellation.post("/api/delete-doc-api/", {
          doc_id: effectiveDocumentId,
          check: "",
        });
        const encodedPdf = response.data?.data;
        if (!encodedPdf) throw new Error("The document did not contain PDF data.");
        const bytes = Uint8Array.from(atob(encodedPdf), (character) => character.charCodeAt(0));
        pdfUrl = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
        setPdfData(pdfUrl);
        if (!editingTemplateId) {
          setDocumentName(
            (
              response.data?.filename ||
              response.data?.file_name ||
              "Electronic Signature Document"
            ).replace(/\.pdf$/i, "")
          );
        }
      } catch (error) {
        setPdfError(requestErrorMessage(error, "Unable to load this PDF document."));
      } finally {
        setLoadingPdf(false);
      }
    };
    fetchPdf();
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [editingTemplateId, effectiveDocumentId]);

  useEffect(() => {
    if (!editingTemplateId) {
      setLoadingTemplate(false);
      return undefined;
    }

    let active = true;
    const fetchTemplate = async () => {
      setLoadingTemplate(true);
      try {
        const response = await api_without_cancellation.get(`/sign/template/${editingTemplateId}/`);
        if (!active) return;

        const responseBody = response.data?.data ?? response.data;
        const template =
          responseBody?.template && typeof responseBody.template === "object"
            ? responseBody.template
            : responseBody;
        const loadedFields = Array.isArray(template?.fields) ? template.fields : [];
        const sourceDocumentId =
          template?.doc_id ??
          template?.document_id ??
          template?.document?.id ??
          template?.for_document?.id;

        if (!docId && !sourceDocumentId) {
          throw new Error("The template did not return its source document identifier.");
        }
        if (sourceDocumentId) setTemplateDocumentId(sourceDocumentId);

        const loadedName =
          template?.name || template?.template_name || "Electronic Signature Document";
        const normalizedFields = loadedFields.map((field, index) => {
          const definition = fieldDefinitions.find(
            (item) => item.semanticKey === field.semantic_key
          );
          return {
            id: `esign-loaded-field-${field.id || index + 1}`,
            label: definition?.label || field.label || field.semantic_key || field.field_type,
            semantic_key: field.semantic_key,
            field_type: getApiFieldType(field.semantic_key),
            position_x: Number(field.position_x) || 0,
            position_y: Number(field.position_y) || 0,
            width: Number(field.width) || definition?.width || 153,
            height: Number(field.height) || definition?.height || 28,
            page_no: Number(field.page_no) || 1,
          };
        });
        const pageId = template?.page_id ?? template?.page?.id ?? "";
        const slotId = template?.slot_id ?? template?.slot?.id ?? template?.page_slot?.id ?? "";
        const isPermanent = Boolean(template?.is_permanent || pageId || slotId);

        setDocumentName(loadedName);
        setFields(normalizedFields);
        setEditingTemplateIsPermanent(isPermanent);
        setMode(isPermanent ? "template" : "one-off");
        setSelectedPageId(pageId === null ? "" : String(pageId));
        setSelectedSlotId(slotId === null ? "" : String(slotId));
        savedTemplatePayloadRef.current = serializeTemplatePayload(
          buildTemplatePayload({
            documentId: docId || sourceDocumentId,
            documentName: loadedName,
            fields: normalizedFields,
            isPermanent,
            pageId,
            slotId,
          })
        );
      } catch (error) {
        if (!active) return;
        setResult({
          tone: "danger",
          message: requestErrorMessage(
            error,
            error.message || "Unable to load this electronic signature template."
          ),
        });
      } finally {
        if (active) setLoadingTemplate(false);
      }
    };

    fetchTemplate();
    return () => {
      active = false;
    };
  }, [docId, editingTemplateId]);

  useEffect(() => {
    const fetchRecipients = async () => {
      setRecipientsLoading(true);
      try {
        const response = await api_without_cancellation.get(`/api/general/case-parties/${caseId}/`);
        const groups = normalizeRecipients(response.data);
        setRecipientGroups(groups);
        const routeClient = groups
          .find((group) => group.type === "Client")
          ?.items.find((item) => String(item.id) === String(clientId));
        setSigners(routeClient ? [routeClient] : []);
        setActiveSignerKey(routeClient?.key || "");
      } catch (error) {
        setResult({
          tone: "danger",
          message: requestErrorMessage(error, "Unable to load recipients for this case."),
        });
        console.error(error);
      } finally {
        setRecipientsLoading(false);
      }
    };

    const fetchLocations = async () => {
      setLocationLoading(true);
      try {
        const response = await api_without_cancellation.get("/sign/esign-page-rows/", {
          params: { page_id: "", case_id: caseId, client_id: clientId },
        });
        const pages = response.data?.pages ?? response.data?.data?.pages ?? response.data?.data;
        setLocations(Array.isArray(pages) ? pages : []);
      } catch (error) {
        setResult({
          tone: "danger",
          message: requestErrorMessage(error, "Unable to load document pages and slots."),
        });
      } finally {
        setLocationLoading(false);
      }
    };

    fetchRecipients();
    fetchLocations();
  }, [caseId, clientId]);

  useEffect(() => {
    const container = pdfViewport;
    if (!container) return undefined;

    const resizeObserver = new ResizeObserver(() => {
      if (!defaultZoom) return;

      const dimensions = pageDimensions[activePage] || LETTER_PAGE;
      const availableWidth = Math.max(100, container.clientWidth - 10);
      const availableHeight = Math.max(100, container.clientHeight - 10);
      const widthScale = availableWidth / dimensions.width;
      const heightScale = availableHeight / dimensions.height;
      const scale =
        defaultZoom === "width"
          ? widthScale
          : defaultZoom === "height"
            ? heightScale
            : Math.min(widthScale, heightScale);

      setPageWidth(dimensions.width * Math.max(scale - 0.05, 0.1));
    });
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [activePage, defaultZoom, pageDimensions, pdfViewport]);

  const updateActivePage = useCallback(() => {
    const container = pdfViewport;
    if (!container || pageRefs.current.size === 0) return;
    const containerRect = container.getBoundingClientRect();
    const centerY = containerRect.top + containerRect.height / 2;
    let closestPage = 1;
    let closestDistance = Number.POSITIVE_INFINITY;
    pageRefs.current.forEach((element, pageNumber) => {
      const rect = element.getBoundingClientRect();
      const distance = Math.abs(rect.top + rect.height / 2 - centerY);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPage = pageNumber;
      }
    });
    setActivePage(closestPage);
  }, [pdfViewport]);

  const scrollPageToView = useCallback((pageNumber, updatePageNumber = setActivePage) => {
    const normalizedPageNumber = Number(pageNumber);
    const pageElement = pageRefs.current.get(normalizedPageNumber);
    if (!pageElement) return;

    pageElement.scrollIntoView({ behavior: "smooth", block: "start" });
    updatePageNumber(normalizedPageNumber);
  }, []);

  const onZoomChange = useCallback(
    (zoomType) => {
      if (zoomType === "toggleDropdown") return;

      const container = pdfViewport;
      const dimensions = pageDimensions[activePage] || LETTER_PAGE;
      const currentScale = pageWidth / dimensions.width;
      const availableWidth = Math.max(100, (container?.clientWidth || pageWidth) - 10);
      const availableHeight = Math.max(100, (container?.clientHeight || 0) - 10);
      let nextScale = currentScale;

      if (zoomType === "in") nextScale = Math.min(currentScale + 0.1, 4);
      else if (zoomType === "out") nextScale = Math.max(currentScale - 0.1, 0.5);
      else if (zoomType === "width") nextScale = availableWidth / dimensions.width;
      else if (zoomType === "height") nextScale = availableHeight / dimensions.height;
      else if (zoomType === "fit") {
        nextScale = Math.min(
          availableWidth / dimensions.width,
          availableHeight / dimensions.height
        );
      } else if (typeof zoomType === "number") nextScale = zoomType;

      setDefaultZoom(["width", "height", "fit"].includes(zoomType) ? zoomType : "");
      setPageWidth(dimensions.width * Math.max(nextScale - (zoomType === "fit" ? 0.05 : 0), 0.1));
    },
    [activePage, pageDimensions, pageWidth, pdfViewport]
  );

  useEffect(() => {
    if (!debouncedPdfSearchTerm.trim() || pdfSearchProgress.isSearching || !pdfSearchPages.length) {
      return;
    }

    scrollPageToView(pdfSearchPages[0], setActivePage);
  }, [debouncedPdfSearchTerm, pdfSearchPages, pdfSearchProgress.isSearching, scrollPageToView]);

  const activePageDimensions = pageDimensions[activePage] || LETTER_PAGE;
  const zoomScale = pageWidth / activePageDimensions.width;

  const locked = Boolean(preparedTemplateId);
  const editorLocked = locked || loadingTemplate;

  const addField = (definition) => {
    if (editorLocked) return;
    const dimensions = pageDimensions[activePage] || LETTER_PAGE;
    const width = definition.width || 153;
    const height = definition.height || 28;
    const newField = {
      id: `esign-field-${nextFieldId.current++}`,
      label: definition.label,
      semantic_key: definition.semanticKey,
      field_type: definition.fieldType,
      page_no: activePage,
      position_x: Math.max(0, ((dimensions.width - width) / 2 / dimensions.width) * 100),
      position_y: Math.max(0, ((dimensions.height - height) / 2 / dimensions.height) * 100),
      width,
      height,
    };
    setFields((current) => [...current, newField]);
    setSelectedFieldId(newField.id);
  };

  const moveField = (field, data) => {
    const dimensions = pageDimensions[field.page_no] || LETTER_PAGE;
    const renderedHeight = pageWidth * (dimensions.height / dimensions.width);
    const renderedFieldWidth = (field.width / dimensions.width) * pageWidth;
    const renderedFieldHeight = (field.height / dimensions.height) * renderedHeight;
    const x = Math.min(Math.max(data.x, 0), Math.max(0, pageWidth - renderedFieldWidth));
    const y = Math.min(Math.max(data.y, 0), Math.max(0, renderedHeight - renderedFieldHeight));
    setFields((current) =>
      current.map((item) =>
        item.id === field.id
          ? { ...item, position_x: (x / pageWidth) * 100, position_y: (y / renderedHeight) * 100 }
          : item
      )
    );
  };

  const resizeField = (field, pointerDownEvent) => {
    if (editorLocked || pointerDownEvent.button !== 0) return;

    pointerDownEvent.preventDefault();
    pointerDownEvent.stopPropagation();
    setSelectedFieldId(field.id);
    resizeCleanupRef.current?.();

    const dimensions = pageDimensions[field.page_no] || LETTER_PAGE;
    const renderedHeight = pageWidth * (dimensions.height / dimensions.width);
    const widthScale = dimensions.width / pageWidth;
    const heightScale = dimensions.height / renderedHeight;
    const fieldX = (field.position_x / 100) * dimensions.width;
    const fieldY = (field.position_y / 100) * dimensions.height;
    const maxWidth = Math.max(0, dimensions.width - fieldX);
    const maxHeight = Math.max(0, dimensions.height - fieldY);
    const minWidth = Math.min(MIN_FIELD_RENDERED_WIDTH * widthScale, maxWidth);
    const minHeight = Math.min(MIN_FIELD_RENDERED_HEIGHT * heightScale, maxHeight);
    const startX = pointerDownEvent.clientX;
    const startY = pointerDownEvent.clientY;
    const startWidth = field.width;
    const startHeight = field.height;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    document.body.style.cursor = "nwse-resize";
    document.body.style.userSelect = "none";

    const handlePointerMove = (pointerMoveEvent) => {
      pointerMoveEvent.preventDefault();
      const width = Math.min(
        Math.max(startWidth + (pointerMoveEvent.clientX - startX) * widthScale, minWidth),
        maxWidth
      );
      const height = Math.min(
        Math.max(startHeight + (pointerMoveEvent.clientY - startY) * heightScale, minHeight),
        maxHeight
      );

      setFields((current) =>
        current.map((item) => (item.id === field.id ? { ...item, width, height } : item))
      );
    };

    const stopResizing = () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopResizing);
      window.removeEventListener("pointercancel", stopResizing);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      if (resizeCleanupRef.current === stopResizing) resizeCleanupRef.current = null;
    };

    resizeCleanupRef.current = stopResizing;
    window.addEventListener("pointermove", handlePointerMove, { passive: false });
    window.addEventListener("pointerup", stopResizing);
    window.addEventListener("pointercancel", stopResizing);
  };

  const getCreatePayload = (permanentOverride, locationOverride) => {
    const isPermanent = permanentOverride ?? mode === "template";
    return buildTemplatePayload({
      documentId: effectiveDocumentId,
      documentName,
      fields,
      isPermanent,
      pageId: locationOverride?.pageId ?? selectedPageId,
      slotId: locationOverride?.slotId ?? selectedSlotId,
    });
  };

  const getSendPayload = () => {
    const defendant = signers.find((signer) => signer.role === "defendant");
    const otherParty = signers.find((signer) => signer.role === "otherparty");

    return {
      case_id: Number(caseId),
      client_id: Number(clientId),
      expires_at: new Date(`${expirationDate}T23:59:59`).toISOString(),
      recipients: signers.map((signer) => {
        const email = signer.email.trim();
        const personName = `${signer.firstName.trim()} ${signer.lastName.trim()}`.trim();

        return {
          role: signer.role,
          name: signer.entityName?.trim() || personName || signer.displayName?.trim() || email,
          email,
        };
      }),
      prefill_context: {
        ...(defendant && { defendant_id: Number(defendant.id) }),
        ...(otherParty && { party_id: Number(otherParty.id) }),
      },
    };
  };

  const validate = (submitMode = mode, locationOverride) => {
    const destinationPageId = locationOverride?.pageId ?? selectedPageId;
    const destinationSlotId = locationOverride?.slotId ?? selectedSlotId;

    if (!documentName.trim()) return "Enter a document name.";
    if (!fields.length) return "Place at least one field on the document.";
    if (!effectiveDocumentId) return "The source document could not be identified.";
    if (submitMode === "one-off") {
      if (!signers.length) return "Add at least one signer.";
      const incompleteSignerIndex = signers.findIndex((signer) => !signer.email.trim());
      if (incompleteSignerIndex !== -1) {
        return `Signer ${incompleteSignerIndex + 1} must have an email address.`;
      }
      const normalizedEmails = signers.map((signer) => signer.email.trim().toLowerCase());
      if (new Set(normalizedEmails).size !== normalizedEmails.length) {
        return "Each signer must have a different email address.";
      }
      if (signers.filter((signer) => signer.role === "defendant").length > 1) {
        return "Only one defendant can be selected.";
      }
      if (signers.filter((signer) => signer.role === "otherparty").length > 1) {
        return "Only one other party can be selected.";
      }
      if (!expirationDate) return "Select an expiration date.";
      if (
        activeTemplateId &&
        editingTemplateIsPermanent &&
        (!destinationPageId || !destinationSlotId)
      ) {
        return "The template must retain a page and document slot before it can be sent.";
      }
    } else {
      if (!destinationPageId || !destinationSlotId) {
        return "Select a page and document slot.";
      }
    }
    return "";
  };

  const sendPreparedTemplate = async (templateId, sendPayload) => {
    try {
      await api_without_cancellation.post(`/sign/send-template/${templateId}/`, sendPayload);
      const successMessage =
        sendPayload.recipients.length === 1
          ? `A signature request has been sent to ${sendPayload.recipients[0].email}.`
          : `${sendPayload.recipients.length} signature requests have been sent successfully.`;
      setResult({
        tone: "success",
        message: successMessage,
      });
      setSendConfirmationMessage(successMessage);
      setPreparedTemplateId(null);
      setPreparedSendPayload(null);
    } catch (error) {
      if (error.response?.status === 500) {
        const affectedEmails = sendPayload.recipients.map(({ email }) => email).join(", ");
        setPreparedTemplateId(null);
        setPreparedSendPayload(null);
        setResult({
          tone: "danger",
          message: `Documents may already exist for ${affectedEmails}, but their emails may have failed. Do not resend them. Contact support with template ${templateId}.`,
          recovery: true,
        });
        return;
      }

      setPreparedSendPayload(sendPayload);
      setResult({
        tone: "warning",
        message: requestErrorMessage(error, "The signature request failed. It is safe to retry."),
        retryable: true,
      });
    }
  };

  const handleSubmit = async (submitMode = mode, locationOverride) => {
    if (submitting) return null;
    if (preparedTemplateId && preparedSendPayload) {
      setSubmitting(true);
      await sendPreparedTemplate(preparedTemplateId, preparedSendPayload);
      setSubmitting(false);
      return { templateId: preparedTemplateId };
    }
    const validationError = validate(submitMode, locationOverride);
    if (validationError) {
      setResult({ tone: "danger", message: validationError });
      return null;
    }

    setSubmitting(true);
    setMode(submitMode);
    setResult(null);
    try {
      if (activeTemplateId) {
        const updatePayload = getCreatePayload(
          submitMode === "one-off" ? editingTemplateIsPermanent : true,
          locationOverride
        );
        const serializedUpdatePayload = serializeTemplatePayload(updatePayload);
        const templateChanged = savedTemplatePayloadRef.current !== serializedUpdatePayload;

        if (templateChanged) {
          await api_without_cancellation.patch(
            `/sign/template/${activeTemplateId}/`,
            updatePayload
          );
          savedTemplatePayloadRef.current = serializedUpdatePayload;
        }

        if (submitMode === "template") {
          const destinationPageId = locationOverride?.pageId ?? selectedPageId;
          const destinationSlotId = locationOverride?.slotId ?? selectedSlotId;
          const destination = locations.find(
            (item) => String(item.page?.id) === String(destinationPageId)
          );
          const destinationSlot = destination?.slots?.find(
            (slot) => String(slot.slot_id ?? slot.id) === String(destinationSlotId)
          );
          setEditingTemplateIsPermanent(true);
          setResult({
            tone: "success",
            message: templateChanged
              ? `Template saved to ${destination?.page?.name || "page"} — ${destinationSlot?.slot_number || ""}. ${destinationSlot?.slot_name || "slot"}.`
              : "The template is already saved in this slot.",
          });
          return { templateId: activeTemplateId };
        }

        const sendPayload = getSendPayload();
        setPreparedTemplateId(activeTemplateId);
        setPreparedSendPayload(sendPayload);
        await sendPreparedTemplate(activeTemplateId, sendPayload);
        return { templateId: activeTemplateId };
      }

      const createPayload = getCreatePayload(submitMode === "template", locationOverride);
      const response = await api_without_cancellation.post("/sign/create-template/", createPayload);
      const responseBody = response.data?.data ?? response.data;
      const returnedTemplate = responseBody?.template;
      const templateId =
        (returnedTemplate && typeof returnedTemplate === "object"
          ? returnedTemplate.id
          : returnedTemplate) ||
        responseBody?.template_id ||
        responseBody?.id;
      if (!templateId) throw new Error("The server did not return a template identifier.");
      setActiveTemplateId(templateId);
      savedTemplatePayloadRef.current = serializeTemplatePayload(createPayload);

      if (submitMode === "template") {
        const destinationPageId = locationOverride?.pageId ?? selectedPageId;
        const destinationSlotId = locationOverride?.slotId ?? selectedSlotId;
        const destination = locations.find(
          (item) => String(item.page?.id) === String(destinationPageId)
        );
        const destinationSlot = destination?.slots?.find(
          (slot) => String(slot.slot_id ?? slot.id) === String(destinationSlotId)
        );
        setEditingTemplateIsPermanent(true);
        setResult({
          tone: "success",
          message: `Template saved to ${destination?.page?.name || "page"} — ${destinationSlot?.slot_number || ""}. ${destinationSlot?.slot_name || "slot"}.`,
        });
      } else {
        const sendPayload = getSendPayload();
        setPreparedTemplateId(templateId);
        setPreparedSendPayload(sendPayload);
        await sendPreparedTemplate(templateId, sendPayload);
      }
      return { templateId };
    } catch (error) {
      setResult({
        tone: "danger",
        message: requestErrorMessage(
          error,
          error.message || "Unable to save the electronic signature document."
        ),
      });
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const handleMoveTemplate = async ({ pageId, slotId }) => {
    if (submitting) return;

    const previousPageId = selectedPageId;
    const previousSlotId = selectedSlotId;
    setSelectedPageId(pageId);
    setSelectedSlotId(slotId);

    const saveResult = await handleSubmit("template", { pageId, slotId });
    if (!saveResult?.templateId) {
      setSelectedPageId(previousPageId);
      setSelectedSlotId(previousSlotId);
      return;
    }

    setLocations((current) =>
      current.map((location) => ({
        ...location,
        slots: (location.slots || []).map((slot) => {
          const isDestination =
            String(location.page?.id) === String(pageId) &&
            String(slot.slot_id ?? slot.id) === String(slotId);
          const containsMovedTemplate =
            String(slot.template_id || "") === String(saveResult.templateId);

          if (isDestination) {
            return {
              ...slot,
              template_id: saveResult.templateId,
              template_name: documentName.trim(),
              status: "",
              sent_at: "",
              signed_at: "",
              signed_document_id: "",
              signers_signed: 0,
              signers_total: 0,
            };
          }

          if (containsMovedTemplate) {
            return {
              ...slot,
              template_id: "",
              template_name: "",
              status: "",
              sent_at: "",
              signed_at: "",
              signed_document_id: "",
              signers_signed: 0,
              signers_total: 0,
            };
          }

          return slot;
        }),
      }))
    );
  };

  return (
    <div className="page-wrapper esign-page-shell">
      <Sidebar />
      <div className="page-container">
        <NavBar flaggedPageName={1} />
        <div className="row mx-auto w-100" id="padding-top-165" style={{ height: "100%" }}>
          <ActionBarComponent
            src="https://simplefirm-bucket.s3.amazonaws.com/static/BP_resources/images/icon/documents-icon-color.svg"
            page_name={`Electronic Signature: ${documentName || "Loading Document"}`}
          />
          <main className="esign-workspace m-t-5">
            <FieldPalette fieldsLocked={editorLocked} onAddField={addField} />
            <section className="esign-document-column">
              <div className="esign-document-viewer-heading">Document Viewer</div>
              <div className="esign-document-search">
                <label htmlFor="esign-document-search">Search in Document:</label>
                <input
                  id="esign-document-search"
                  name="documentSearch"
                  type="text"
                  autoComplete="off"
                  className="form-control document-search-text-color"
                  placeholder="Type Search Terms to Only Display Pages With Those Terms"
                  value={pdfSearchTerm}
                  onChange={(event) => setPdfSearchTerm(event.target.value)}
                />
              </div>
              {pdfSearchProgress.isSearching && (
                <div className="esign-document-search-progress" role="status">
                  Searching… {pdfSearchProgress.done}/{pdfSearchProgress.total}
                </div>
              )}
              <div className="esign-pdf-scroll" ref={setPdfViewport} onScroll={updateActivePage}>
                {loadingPdf && <div className="esign-empty-state">Loading PDF…</div>}
                {pdfError && <div className="esign-empty-state esign-error-state">{pdfError}</div>}
                {pdfData && (
                  <Document
                    file={pdfData}
                    options={PDF_LOAD_OPTIONS}
                    onLoadSuccess={(pdf) => {
                      setPdfDocument(pdf);
                      setNumPages(pdf.numPages);
                    }}
                    onLoadError={() => setPdfError("Unable to render this PDF document.")}
                    loading={<div className="esign-empty-state">Preparing pages…</div>}
                  >
                    {Array.from({ length: numPages }, (_, index) => {
                      const pageNumber = index + 1;
                      const dimensions = pageDimensions[pageNumber] || LETTER_PAGE;
                      const renderedHeight = pageWidth * (dimensions.height / dimensions.width);
                      return (
                        <div
                          key={pageNumber}
                          ref={(element) => {
                            if (element) pageRefs.current.set(pageNumber, element);
                            else pageRefs.current.delete(pageNumber);
                          }}
                          className="esign-pdf-page"
                          style={{ width: pageWidth, height: renderedHeight }}
                        >
                          <Page
                            pageNumber={pageNumber}
                            width={pageWidth}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            onLoadSuccess={(page) => {
                              const viewport = page.getViewport({ scale: 1 });
                              setPageDimensions((current) => ({
                                ...current,
                                [pageNumber]: { width: viewport.width, height: viewport.height },
                              }));
                            }}
                          />
                          <div className="esign-field-overlay">
                            {fields
                              .filter((field) => field.page_no === pageNumber)
                              .map((field) => {
                                const fieldWidth = (field.width / dimensions.width) * pageWidth;
                                const fieldHeight =
                                  (field.height / dimensions.height) * renderedHeight;
                                return (
                                  <Draggable
                                    key={field.id}
                                    bounds="parent"
                                    cancel=".esign-field-action"
                                    disabled={editorLocked}
                                    position={{
                                      x: (field.position_x / 100) * pageWidth,
                                      y: (field.position_y / 100) * renderedHeight,
                                    }}
                                    onStart={() => setSelectedFieldId(field.id)}
                                    onDrag={(_event, data) => moveField(field, data)}
                                    onStop={(_event, data) => moveField(field, data)}
                                  >
                                    <div
                                      className={`esign-placed-field ${selectedFieldId === field.id ? "esign-placed-field-selected" : ""}`}
                                      style={{ width: fieldWidth, height: fieldHeight }}
                                      onClick={() => setSelectedFieldId(field.id)}
                                    >
                                      <span>{field.label}</span>
                                      {!editorLocked && selectedFieldId === field.id && (
                                        <>
                                          <button
                                            type="button"
                                            className="esign-field-action esign-field-delete"
                                            aria-label={`Remove ${field.label}`}
                                            onMouseDown={(event) => event.stopPropagation()}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              setFields((current) =>
                                                current.filter((item) => item.id !== field.id)
                                              );
                                              setSelectedFieldId(null);
                                            }}
                                          >
                                            ×
                                          </button>
                                          <button
                                            type="button"
                                            className="esign-field-action esign-field-resize-handle"
                                            aria-label={`Resize ${field.label}`}
                                            title="Drag to resize"
                                            onPointerDown={(event) => resizeField(field, event)}
                                          />
                                        </>
                                      )}
                                    </div>
                                  </Draggable>
                                );
                              })}
                          </div>
                        </div>
                      );
                    })}
                  </Document>
                )}
              </div>
            </section>
            <SetupPanel
              documentName={documentName}
              setDocumentName={setDocumentName}
              mode={mode}
              locked={editorLocked}
              loadingTemplate={loadingTemplate}
              activeTemplateId={activeTemplateId}
              recipientGroups={recipientGroups}
              recipientsLoading={recipientsLoading}
              signers={signers}
              setSigners={setSigners}
              activeSignerKey={activeSignerKey}
              setActiveSignerKey={setActiveSignerKey}
              expirationDate={expirationDate}
              setExpirationDate={setExpirationDate}
              locations={locations}
              locationLoading={locationLoading}
              selectedPageId={selectedPageId}
              selectedSlotId={selectedSlotId}
              handleMoveTemplate={handleMoveTemplate}
              result={result}
              fieldsCount={fields.length}
              submitting={submitting}
              preparedTemplateId={preparedTemplateId}
              signerCount={signers.length}
              handleSubmit={handleSubmit}
            />
            <PdfNavigation
              alignWithDocument
              documentViewport={pdfViewport}
              showSearch={false}
              pageNumber={activePage}
              setPageNumber={setActivePage}
              totalPages={numPages}
              onZoomChange={onZoomChange}
              zoomScale={zoomScale}
              scrollPageToView={scrollPageToView}
              isChatOpen={false}
            />
          </main>
          <Footer />
        </div>
      </div>
      <Modal
        show={Boolean(sendConfirmationMessage)}
        onHide={() => setSendConfirmationMessage("")}
        centered
        contentClassName="custom-modal-new-provider"
        style={{ zIndex: 1000000 }}
      >
        <Modal.Header className="text-center height-25 p-0 bg-primary popup-heading-color justify-content-center">
          <Modal.Title
            className="mx-auto height-25 font-weight-semibold text-uppercase popup-heading-color d-flex align-items-center"
            style={{ fontSize: "14px" }}
          >
            Signature Request Sent
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 text-center">
          <p className="m-0 font-weight-semibold">{sendConfirmationMessage}</p>
        </Modal.Body>
        <Modal.Footer className="p-1 mt-0 justify-content-center" style={{ borderTop: 0 }}>
          <div />
          <Button
            type="button"
            variant="success"
            className="height-25 py-0"
            onClick={() => setSendConfirmationMessage("")}
          >
            OK
          </Button>
          <div />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const FieldPalette = ({ fieldsLocked, onAddField }) => (
  <aside className="esign-panel esign-field-panel">
    <span
      style={{
        fontSize: "14px",
        fontWeight: "600",
      }}
      className="bg-primary height-25 text-white d-flex align-items-center justify-content-center"
    >
      LIBRARY
    </span>
    <div className="esign-panel-scroll">
      {FIELD_GROUPS.map((group) => (
        <section key={group.title} className="esign-field-group">
          <span
            className="height-25 d-flex align-items-center justify-content-center"
            style={{
              fontWeight: "600",
              color: "var(--primary)",
              background: "var(--primary-25)",
              textTransform: "uppercase",
              fontSize: "14px",
              marginBottom: "5px",
            }}
          >
            {group.title}
          </span>
          <div className="esign-field-grid">
            {group.fields.map((field) => (
              <button
                type="button"
                key={field.semanticKey}
                disabled={fieldsLocked}
                className="esign-field-button height-25 font-weight-semibold cursor-pointer"
                onClick={() => onAddField(field)}
                title={getPaletteFieldLabel(field.label)}
              >
                {getPaletteFieldLabel(field.label)}
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  </aside>
);

const SetupPanel = (props) => {
  const {
    documentName,
    setDocumentName,
    locked,
    loadingTemplate,
    activeTemplateId,
    recipientGroups,
    recipientsLoading,
    signers,
    setSigners,
    activeSignerKey,
    setActiveSignerKey,
    expirationDate,
    setExpirationDate,
    locations,
    locationLoading,
    selectedPageId,
    selectedSlotId,
    handleMoveTemplate,
    result,
    submitting,
    preparedTemplateId,
    signerCount,
    handleSubmit,
  } = props;

  return (
    <aside className="esign-panel esign-setup-panel">
      <span
        style={{
          fontSize: "14px",
          fontWeight: "600",
          textTransform: "uppercase",
        }}
        className="bg-primary height-25 text-white d-flex align-items-center justify-content-center"
      >
        Signing Configuration
      </span>
      <div className="esign-panel-scroll esign-setup-scroll">
        <section className="esign-sidebar-section">
          <label className="esign-sidebar-heading" htmlFor="esign-document-name">
            DOCUMENT NAME
          </label>
          <div className="esign-document-name-row">
            <input
              id="esign-document-name"
              className="form-control height-25 p-l-5 rounded-0"
              placeholder="Document Name"
              value={documentName}
              disabled={locked || submitting}
              onChange={(event) => setDocumentName(event.target.value)}
            />
          </div>
        </section>

        <OneOffForm
          recipientGroups={recipientGroups}
          recipientsLoading={recipientsLoading}
          signers={signers}
          setSigners={setSigners}
          activeSignerKey={activeSignerKey}
          setActiveSignerKey={setActiveSignerKey}
          expirationDate={expirationDate}
          setExpirationDate={setExpirationDate}
          locked={locked}
        />
        <TemplateLocationForm
          locations={locations}
          locationLoading={locationLoading}
          selectedPageId={selectedPageId}
          selectedSlotId={selectedSlotId}
          activeTemplateId={activeTemplateId}
          locked={locked || submitting}
          onMoveTemplate={handleMoveTemplate}
        />
        {result && (
          <div className={`esign-result esign-result-${result.tone}`} role="status">
            {result.message}
          </div>
        )}
      </div>
      <button
        type="button"
        className="btn btn-success m-b-5 mx-1 d-flex align-items-center justify-content-center height-25 py-0"
        style={{
          fontWeight: "600",
        }}
        disabled={loadingTemplate || submitting}
        onClick={() => handleSubmit("one-off")}
      >
        {loadingTemplate
          ? "Loading…"
          : submitting
            ? "Working…"
            : preparedTemplateId
              ? "Retry Send"
              : signerCount > 1
                ? `Send to ${signerCount} Signers`
                : "Send for Signature"}
      </button>
    </aside>
  );
};

const SigningDetail = ({ label, value }) => {
  const text = value?.trim();

  return (
    <div className="esign-detail">
      <span className="esign-detail-label">{label}</span>
      <div className="esign-detail-value">
        <span className={text ? undefined : "esign-detail-placeholder"}>{text || "—"}</span>
      </div>
    </div>
  );
};

const OneOffForm = ({
  recipientGroups,
  recipientsLoading,
  signers,
  setSigners,
  activeSignerKey,
  setActiveSignerKey,
  expirationDate,
  setExpirationDate,
  locked,
}) => {
  const activeSigner = signers.find((signer) => signer.key === activeSignerKey) || null;
  const removeSigner = (signerKey) => {
    const remainingSigners = signers.filter((signer) => signer.key !== signerKey);
    setSigners(remainingSigners);
    if (activeSignerKey === signerKey) {
      setActiveSignerKey(remainingSigners[0]?.key || "");
    }
  };

  return (
    <section className="esign-form-section">
      <span
        className="d-flex align-items-center justify-content-center  height-25 text-primary"
        style={{
          fontWeight: "600",
          fontSize: "14px",
          background: "var(--primary-25)",
          textTransform: "uppercase",
        }}
      >
        Signers
      </span>
      <div className="esign-form-section-body">
        <label className="esign-detail-label" htmlFor="esign-recipient">
          Add Signer
        </label>
        <select
          id="esign-recipient"
          aria-label="Add signer"
          className="form-select height-25 rounded-0 py-0"
          value=""
          disabled={recipientsLoading || locked}
          onChange={(event) => {
            const person = recipientGroups
              .flatMap((group) => group.items)
              .find((item) => item.key === event.target.value);
            if (!person) return;
            const roleAlreadySelected =
              SINGLE_SELECTION_ROLES.has(person.role) &&
              signers.some((signer) => signer.role === person.role);
            if (roleAlreadySelected) return;
            setSigners((current) =>
              current.some((signer) => signer.key === person.key) ||
              (SINGLE_SELECTION_ROLES.has(person.role) &&
                current.some((signer) => signer.role === person.role))
                ? current
                : [...current, person]
            );
            setActiveSignerKey(person.key);
          }}
        >
          <option value="">{recipientsLoading ? "Loading recipients…" : "Select a signer"}</option>
          {recipientGroups.map((group) => (
            <optgroup key={group.type} label={group.label}>
              {group.items.map((person) => {
                const alreadySelected = signers.some((signer) => signer.key === person.key);
                const roleUnavailable =
                  !alreadySelected &&
                  SINGLE_SELECTION_ROLES.has(person.role) &&
                  signers.some((signer) => signer.role === person.role);
                return (
                  <option
                    key={person.key}
                    value={person.key}
                    disabled={alreadySelected || roleUnavailable}
                  >
                    {person.displayName}
                    {alreadySelected ? " — Added" : roleUnavailable ? " — Only one allowed" : ""}
                  </option>
                );
              })}
            </optgroup>
          ))}
        </select>

        {signers.length > 0 ? (
          <div className="esign-signer-list" aria-label="Selected signers">
            {signers.map((signer, index) => {
              const name =
                signer.entityName?.trim() ||
                `${signer.firstName} ${signer.lastName}`.trim() ||
                signer.displayName;
              return (
                <div
                  key={signer.key}
                  className={`esign-signer-item ${signer.key === activeSignerKey ? "active" : ""}`}
                >
                  <button
                    type="button"
                    className="esign-signer-select"
                    disabled={locked}
                    onClick={() => setActiveSignerKey(signer.key)}
                  >
                    <span>{index + 1}</span>
                    <span>
                      <strong>{name}</strong>
                      <small>{signer.email || "Email required"}</small>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="esign-signer-remove"
                    disabled={locked}
                    aria-label={`Remove ${name}`}
                    title={`Remove ${name}`}
                    onClick={() => removeSigner(signer.key)}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="esign-section-help">Add one or more people who need to sign.</p>
        )}

        {activeSigner && (
          <div className="esign-signer-editor">
            <p className="esign-signer-editor-title">
              Signer {signers.findIndex((signer) => signer.key === activeSignerKey) + 1} details
            </p>
            {activeSigner.isEntity ? (
              <SigningDetail label="Entity Name" value={activeSigner.entityName} />
            ) : (
              <div className="esign-name-row">
                <SigningDetail label="First Name" value={activeSigner.firstName} />
                <SigningDetail label="Last Name" value={activeSigner.lastName} />
              </div>
            )}
            <SigningDetail label="Email Address" value={activeSigner.email} />
          </div>
        )}

        <label className="esign-form-label">
          Expiration Date
          <input
            type="date"
            className="form-control height-25   p-l-5  rounded-0"
            value={expirationDate}
            disabled={locked}
            onChange={(event) => setExpirationDate(event.target.value)}
          />
        </label>
      </div>
    </section>
  );
};

const TemplateLocationForm = ({
  locations,
  locationLoading,
  selectedPageId,
  selectedSlotId,
  activeTemplateId,
  locked,
  onMoveTemplate,
}) => {
  const [openPageId, setOpenPageId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [replacementSlot, setReplacementSlot] = useState(null);
  const locationPages = locations
    .map((location) => ({
      ...location,
      numberedSlots: (location.slots || []).filter((slot) => Number(slot.slot_number) !== 0),
    }))
    .filter((location) => location.numberedSlots.length > 0);
  const columnBreak = Math.ceil(locationPages.length / 2);
  const locationColumns = [locationPages.slice(0, columnBreak), locationPages.slice(columnBreak)];
  const showPageMenu = (event, pageId) => {
    const pageRow = event.currentTarget.closest(".esign-move-page") || event.currentTarget;
    const pageRowRect = pageRow.getBoundingClientRect();

    setMenuPosition({
      top: pageRowRect.top,
      right: window.innerWidth - pageRowRect.right + 145,
    });
    setOpenPageId(pageId);
  };
  const selectSlot = (slotSelection) => {
    setOpenPageId(null);
    onMoveTemplate(slotSelection);
  };

  return (
    <>
      <section className="esign-form-section esign-move-section">
        <span
          className="d-flex align-items-center justify-content-center m-t-5 height-25 text-primary"
          style={{
            fontWeight: "600",
            fontSize: "14px",
            background: "var(--primary-25)",
            textTransform: "uppercase",
          }}
        >
          Save Template
        </span>
        {locationLoading ? (
          <div className="esign-move-loading">Loading pages…</div>
        ) : locationPages.length === 0 ? (
          <div className="esign-move-empty">No document slots available.</div>
        ) : (
          <div className="esign-move-page-grid" aria-label="Template document location">
            {locationColumns.map((column, columnIndex) => (
              <ul key={columnIndex} className="esign-move-page-column">
                {column.map((location, rowIndex) => {
                  const pageId = String(location.page?.id ?? "");
                  const menuOpen = openPageId === pageId;
                  const pageSelected = String(selectedPageId) === pageId;
                  const pageIcon = location.page?.page_icon || location.page?.icon;

                  return (
                    <li
                      key={pageId}
                      className={"esign-move-page"}
                      onMouseEnter={(event) => showPageMenu(event, pageId)}
                      onMouseLeave={() => setOpenPageId(null)}
                      style={{
                        background: rowIndex % 2 === 0 ? "var(--primary-2)" : "var(--primary-4)",
                      }}
                    >
                      <button
                        type="button"
                        className="esign-move-page-trigger"
                        disabled={locationLoading || locked}
                        aria-expanded={menuOpen}
                        onFocus={(event) => showPageMenu(event, pageId)}
                        onClick={(event) => {
                          if (menuOpen) setOpenPageId(null);
                          else showPageMenu(event, pageId);
                        }}
                      >
                        {pageIcon && <img src={pageIcon} alt="" aria-hidden="true" />}
                        <span className="esign-move-page-name">{location.page?.name}</span>
                      </button>

                      <ul
                        className="esign-move-slot-menu"
                        style={{
                          position: "fixed",
                          right: `${menuPosition.right}px`,
                          top: `${menuPosition.top}px`,
                          zIndex: 1050,
                          display: menuOpen ? "block" : "none",
                        }}
                      >
                        <li className="esign-move-slot-heading">
                          <span>DOC SLOT BUTTONS</span>
                          <span
                            className="esign-move-caret"
                            aria-hidden="true"
                            style={{
                              display: "inline-block",
                              width: "17px",
                              height: "17px",
                              color: "#19395F",
                              transform: "rotate(180deg)",
                              backgroundImage: menuOpen
                                ? "url(\"data:image/svg+xml,<svg width='17' height='17' viewBox='0 0 17 34' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M0.00634766 33.9993L0.00634766 24.7993L7.8602 16.9992L0.00634766 9.19912L0.00634766 -0.000884091L17.0063 16.9992L0.00634766 33.9993Z' fill='%2319395f'/></svg>%0A\")"
                                : "",
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "contain",
                              marginRight: "0px",
                            }}
                          />
                        </li>
                        {location.numberedSlots.map((slot, slotIndex) => {
                          const slotId = String(slot.slot_id ?? slot.id ?? "");
                          const hasTemplate = Boolean(slot.template_id);
                          const replacesTemplate =
                            hasTemplate &&
                            String(slot.template_id) !== String(activeTemplateId || "");
                          const selected = pageSelected && String(selectedSlotId) === slotId;
                          const slotSelection = {
                            pageId,
                            pageName: location.page?.name,
                            slotId,
                            slot,
                          };

                          return (
                            <li
                              key={slotId}
                              className="esign-move-slot-row"
                              style={{
                                background:
                                  slotIndex % 2 === 0 ? "var(--primary-2)" : "var(--primary-4)",
                              }}
                            >
                              <button
                                type="button"
                                className={`esign-move-slot ${hasTemplate ? "has-template" : "is-empty"} ${selected ? "selected" : ""}`}
                                disabled={locked}
                                title={
                                  hasTemplate
                                    ? `${slot.template_name || "Attached template"}${slot.status ? ` (${slot.status})` : ""}`
                                    : slot.slot_name || "Available"
                                }
                                onClick={() => {
                                  if (replacesTemplate) {
                                    setReplacementSlot(slotSelection);
                                    setOpenPageId(null);
                                    return;
                                  }
                                  selectSlot(slotSelection);
                                }}
                              >
                                <span className="esign-move-slot-icon" aria-hidden="true">
                                  {hasTemplate ? (
                                    <i className="ic ic-15 ic-file-colored" />
                                  ) : (
                                    <img src={UploadIcon} alt="" />
                                  )}
                                </span>
                                <span>{slot.slot_number}.</span>
                                <span>
                                  {hasTemplate
                                    ? slot.template_name || "Attached template"
                                    : slot.slot_name || "Available"}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            ))}
          </div>
        )}
      </section>

      <Modal
        show={Boolean(replacementSlot)}
        onHide={() => setReplacementSlot(null)}
        centered
        backdrop="static"
        contentClassName="custom-modal-new-provider"
        style={{ zIndex: 1000000 }}
      >
        <Modal.Header className="text-center height-25 p-0 bg-primary popup-heading-color justify-content-center">
          <Modal.Title
            className="mx-auto height-25 font-weight-semibold text-uppercase popup-heading-color d-flex align-items-center"
            style={{ fontSize: "14px" }}
          >
            Replace Existing Template
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 text-center">
          <p className="m-0 font-weight-semibold">
            {replacementSlot?.slot?.template_name || "The existing template"} is already attached to
            {` ${replacementSlot?.pageName || "this page"}, slot ${replacementSlot?.slot?.slot_number || ""}. `}
            Continuing will delete the existing template and immediately move this template into its
            place.
          </p>
        </Modal.Body>
        <Modal.Footer className="p-1 mt-0 d-flex justify-content-between" style={{ borderTop: 0 }}>
          <Button
            type="button"
            variant="secondary"
            className="height-25 py-0"
            onClick={() => setReplacementSlot(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="success"
            className="height-25 py-0"
            onClick={() => {
              selectSlot(replacementSlot);
              setReplacementSlot(null);
            }}
          >
            Replace Template
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ESignPage;
